import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SegmentedToggle } from "../../../components/common/SegmentedToggle";
import { CARD, CardTitle } from "../../dashboard/profile/profileCard";
import teamService from "../../../services/teamService";

// Roster / Schedule / Statistics tabs on a coach's team pages.

export const TEAM_TABS = [
  { value: "roster", label: "Roster" },
  { value: "schedule", label: "Schedule" },
  { value: "statistics", label: "Statistics" },
];

// Stat columns the Statistics tab's player table starts with.
export const DEFAULT_STAT_COLUMNS = {
  hitting: ['avg', 'obp', 'slg', 'ops', 'hr', 'rbi', 'runs', 'hits', 'bb', 'k', 'sb'],
  pitching: ['era', 'whip', 'ip', 'w', 'l', 'sv', 'so', 'bb', 'h', 'er'],
};

export const TeamTabs = ({ value, onChange }) => (
  <div className="flex justify-center sm:justify-start">
    <SegmentedToggle options={TEAM_TABS} value={value} onChange={onChange} ariaLabel="Team sections" />
  </div>
);

// ─── Schedule ────────────────────────────────────────────────────────────────

// Schedules will come from PrestoSports (the Event model in the presto sync).
// Until that endpoint exists the tab shows SAMPLE_GAMES, in the same shape the
// API will send, with a "Sample data" badge. Pass real events as `games`.
const DAY = 24 * 60 * 60 * 1000;
const sampleGame = (dayOffset, hour, opponentName, home, conference, teamScore, opponentScore, extra = {}) => {
  const date = new Date(Date.now() + dayOffset * DAY);
  date.setHours(hour, 0, 0, 0);
  const final = teamScore != null;
  return {
    eventId: `sample-${dayOffset}`,
    date: date.toISOString(),
    opponentName,
    home,
    conference,
    neutral: false,
    status: final ? "final" : "scheduled",
    result: final ? (teamScore > opponentScore ? "W" : teamScore < opponentScore ? "L" : "T") : null,
    teamScore,
    opponentScore,
    innings: null,
    postponed: false,
    cancelled: false,
    ...extra,
  };
};

const SAMPLE_GAMES = [
  sampleGame(-24, 13, "Blinn College", true, false, 7, 4),
  sampleGame(-21, 15, "San Jacinto College", false, false, 3, 5),
  sampleGame(-17, 13, "Navarro College", true, true, 9, 2),
  sampleGame(-14, 14, "Weatherford College", false, true, 4, 3, { innings: 10 }),
  sampleGame(-10, 12, "Hill College", true, true, 6, 6),
  sampleGame(-7, 18, "McLennan CC", false, true, 2, 8),
  sampleGame(-3, 13, "Temple College", true, true, 11, 1),
  sampleGame(2, 13, "Grayson College", true, true, null, null),
  sampleGame(5, 18, "Cisco College", false, true, null, null, { postponed: true, status: "postponed" }),
  sampleGame(9, 12, "Ranger College", true, false, null, null),
  sampleGame(12, 15, "Panola College", false, false, null, null, { neutral: true }),
  sampleGame(16, 13, "Paris Junior College", true, true, null, null),
];

const SCHEDULE_FILTERS = [
  { value: "all", label: "All" },
  { value: "conference", label: "Conference" },
];

const isFinal = (game) => game.result != null;
const gameDate = (game) => new Date(game.date);

const recordOf = (games) => {
  const count = (r) => games.filter((g) => g.result === r).length;
  const w = count("W");
  const l = count("L");
  const t = count("T");
  return t ? `${w}-${l}-${t}` : `${w}-${l}`;
};

const RESULT_STYLES = {
  W: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
  L: "bg-red-500/15 text-red-600 dark:text-red-400",
  T: "bg-jp-section text-jp-muted",
};

const GameRow = ({ game }) => {
  const date = gameDate(game);
  const where = game.home || game.neutral ? "vs" : "@";
  const location = game.neutral ? "Neutral" : game.home ? "Home" : "Away";

  let outcome;
  if (isFinal(game)) {
    outcome = (
      <span className="flex items-center gap-2">
        <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-extrabold ${RESULT_STYLES[game.result]}`}>
          {game.result}
        </span>
        <span className="text-jp-ink font-extrabold tabular-nums">
          {game.teamScore}-{game.opponentScore}
          {game.innings ? <span className="text-jp-muted text-xs font-semibold"> ({game.innings})</span> : null}
        </span>
      </span>
    );
  } else if (game.cancelled || game.postponed) {
    outcome = (
      <span className="text-xs font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400">
        {game.cancelled ? "Cancelled" : "Postponed"}
      </span>
    );
  } else {
    outcome = (
      <span className="text-jp-muted text-sm font-semibold tabular-nums">
        {date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
      </span>
    );
  }

  return (
    <li className="flex items-center gap-4 rounded-xl px-4 py-3 bg-jp-section border border-jp-outline/50">
      <div className="w-12 shrink-0 text-center">
        <p className="text-jp-muted text-[10px] font-bold uppercase tracking-widest">
          {date.toLocaleDateString([], { month: "short" })}
        </p>
        <p className="text-jp-ink text-xl font-extrabold leading-none tabular-nums">{date.getDate()}</p>
        <p className="text-jp-muted text-[10px] font-semibold uppercase">{date.toLocaleDateString([], { weekday: "short" })}</p>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-jp-ink text-sm font-semibold truncate">
          <span className="text-jp-muted font-bold mr-1">{where}</span>
          {game.opponentName || "TBA"}
        </p>
        <p className="mt-1 flex flex-wrap items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-jp-muted">
          <span>{location}</span>
          {game.conference && (
            <span className="px-1.5 py-0.5 rounded bg-jp-accent/15 text-jp-accent-ink">Conference</span>
          )}
        </p>
      </div>
      <div className="shrink-0">{outcome}</div>
    </li>
  );
};

export const TeamSchedule = ({ games = SAMPLE_GAMES, isSample = games === SAMPLE_GAMES }) => {
  const [filter, setFilter] = useState("all");

  const sorted = [...games].sort((a, b) => gameDate(a) - gameDate(b));
  const results = sorted.filter(isFinal);
  const upcoming = sorted.filter((g) => !isFinal(g));
  const shown = filter === "conference" ? sorted.filter((g) => g.conference) : sorted;
  const nextGame = upcoming.find((g) => !g.cancelled && !g.postponed);

  const conference = results.filter((g) => g.conference);
  const summary = [
    { label: "Overall", value: recordOf(results) },
    { label: "Conference", value: recordOf(conference) },
    { label: "Home", value: recordOf(results.filter((g) => g.home && !g.neutral)) },
    { label: "Away", value: recordOf(results.filter((g) => !g.home || g.neutral)) },
  ];

  if (!games.length) {
    return (
      <section className={`${CARD} text-center py-12`}>
        <p className="text-lg font-bold text-jp-ink">No games scheduled yet</p>
        <p className="text-sm mt-2 text-jp-muted">Game dates, opponents, and results for this team will show here.</p>
      </section>
    );
  }

  return (
    <div className="space-y-6 text-left">
      {isSample && (
        <p className="flex items-center gap-2 text-jp-muted text-xs">
          <span className="px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-400 font-bold uppercase tracking-wider text-[10px]">
            Sample data
          </span>
          The real schedule from PrestoSports will fill in here once it's connected.
        </p>
      )}

      <section className={CARD}>
        <CardTitle title="Record" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {summary.map((metric) => (
            <div key={metric.label} className="rounded-xl p-3 bg-jp-section border border-jp-outline/50 text-center">
              <p className="text-jp-muted text-[10px] font-bold uppercase tracking-widest mb-1">{metric.label}</p>
              <p className="text-jp-accent-ink text-lg font-extrabold tabular-nums">{metric.value}</p>
            </div>
          ))}
        </div>
        {nextGame && (
          <p className="mt-4 text-sm text-jp-muted">
            <span className="font-bold text-jp-ink">Next game:</span>{" "}
            {gameDate(nextGame).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })}{" "}
            {nextGame.home || nextGame.neutral ? "vs" : "@"} {nextGame.opponentName}
          </p>
        )}
      </section>

      <section className={CARD}>
        <CardTitle title="Games">
          <SegmentedToggle options={SCHEDULE_FILTERS} value={filter} onChange={setFilter} ariaLabel="Filter games" />
        </CardTitle>
        {shown.length ? (
          <ul className="space-y-2">
            {shown.map((game) => (
              <GameRow key={game.eventId} game={game} />
            ))}
          </ul>
        ) : (
          <p className="text-sm text-jp-muted text-center py-6">
            No conference games scheduled yet.
          </p>
        )}
      </section>
    </div>
  );
};

// ─── Statistics ──────────────────────────────────────────────────────────────

const SEASON_YEAR = 2025;
const ROSTER_LIMIT = 200; // whole roster in one request, so totals include everyone

// First number found under any of the given names (stat names vary by source).
const num = (stats, keys) => {
  for (const key of keys) {
    const v = parseFloat(stats?.[key]);
    if (!Number.isNaN(v)) return v;
  }
  return 0;
};

// Innings are written in baseball notation: 45.2 means 45 and 2/3.
const inningsToOuts = (ip) => {
  const whole = Math.trunc(ip);
  return whole * 3 + Math.round((ip - whole) * 10);
};
const outsToInnings = (outs) => `${Math.floor(outs / 3)}.${outs % 3}`;

const rate = (value) => (value >= 1 ? value.toFixed(3) : value.toFixed(3).replace(/^0/, ""));
const seasonStats = (list) => (Array.isArray(list) ? list[0] : list) || null;
const playerName = (p) => p.name || `${p.firstName || ""} ${p.lastName || ""}`.trim() || "Player";

const BAT = {
  ab: ["at_bats", "ab", "atBats"],
  h: ["hits", "h"],
  "2b": ["doubles", "2b"],
  "3b": ["triples", "3b"],
  hr: ["home_runs", "hr", "homeRuns"],
  r: ["runs", "r"],
  rbi: ["rbi"],
  bb: ["walks", "bb"],
  hbp: ["hit_by_pitch", "hbp"],
  sf: ["sacrifice_flies", "sf"],
  so: ["strikeouts", "so"],
  sb: ["stolen_bases", "sb"],
  tb: ["total_bases", "tb"],
};
const PIT = {
  ip: ["innings_pitched", "ip"],
  er: ["earned_runs", "er"],
  h: ["hits_allowed", "h"],
  bb: ["walks_allowed", "bb"],
  so: ["strikeouts_pitched", "so"],
  w: ["wins", "w"],
  l: ["losses", "l"],
  sv: ["saves", "sv"],
  hr: ["home_runs_allowed", "hr"],
};

const buildTeamStats = (players) => {
  const hitters = [];
  const pitchers = [];

  players.forEach((p) => {
    const bat = seasonStats(p.battingStats);
    if (bat) {
      const row = Object.fromEntries(Object.entries(BAT).map(([k, keys]) => [k, num(bat, keys)]));
      if (!row.tb) row.tb = row.h + row["2b"] + 2 * row["3b"] + 3 * row.hr;
      if (row.ab > 0) hitters.push({ player: p, ...row });
    }
    const pit = seasonStats(p.pitchingStats);
    if (pit) {
      const row = Object.fromEntries(Object.entries(PIT).map(([k, keys]) => [k, num(pit, keys)]));
      row.outs = inningsToOuts(row.ip);
      if (row.outs > 0) pitchers.push({ player: p, ...row });
    }
  });

  const sum = (rows, key) => rows.reduce((total, row) => total + row[key], 0);
  const b = Object.fromEntries(Object.keys(BAT).map((k) => [k, sum(hitters, k)]));
  const outs = sum(pitchers, "outs");
  const pit = Object.fromEntries(Object.keys(PIT).map((k) => [k, sum(pitchers, k)]));

  const obpDenom = b.ab + b.bb + b.hbp + b.sf;
  const avg = b.ab ? b.h / b.ab : 0;
  const obp = obpDenom ? (b.h + b.bb + b.hbp) / obpDenom : 0;
  const slg = b.ab ? b.tb / b.ab : 0;
  const innings = outs / 3;

  // Rate leaders need enough playing time: a quarter of the team's busiest player.
  const minAb = Math.max(10, Math.max(0, ...hitters.map((h) => h.ab)) * 0.25);
  const minOuts = Math.max(15, Math.max(0, ...pitchers.map((p) => p.outs)) * 0.25);
  const qualifiedHitters = hitters.filter((h) => h.ab >= minAb);
  const qualifiedPitchers = pitchers.filter((p) => p.outs >= minOuts);
  const top = (rows, score, format, lowestFirst = false) => {
    const sorted = [...rows].sort((x, y) => (lowestFirst ? score(x) - score(y) : score(y) - score(x)));
    const best = sorted[0];
    return best && (lowestFirst || score(best) > 0) ? { player: best.player, value: format(score(best)) } : null;
  };

  return {
    hasHitting: hitters.length > 0,
    hasPitching: pitchers.length > 0,
    hitting: [
      { label: "AVG", value: rate(avg) },
      { label: "OBP", value: rate(obp) },
      { label: "SLG", value: rate(slg) },
      { label: "OPS", value: rate(obp + slg) },
      { label: "R", value: b.r },
      { label: "H", value: b.h },
      { label: "HR", value: b.hr },
      { label: "RBI", value: b.rbi },
      { label: "BB", value: b.bb },
      { label: "SB", value: b.sb },
    ],
    pitching: [
      { label: "ERA", value: innings ? ((pit.er * 9) / innings).toFixed(2) : "-" },
      { label: "WHIP", value: innings ? ((pit.bb + pit.h) / innings).toFixed(2) : "-" },
      { label: "IP", value: outsToInnings(outs) },
      { label: "W-L", value: `${pit.w}-${pit.l}` },
      { label: "SV", value: pit.sv },
      { label: "K", value: pit.so },
      { label: "BB", value: pit.bb },
      { label: "H", value: pit.h },
      { label: "HR", value: pit.hr },
      { label: "K/9", value: innings ? ((pit.so * 9) / innings).toFixed(1) : "-" },
    ],
    leaders: [
      { label: "Batting Average", ...top(qualifiedHitters, (h) => h.h / h.ab, rate) },
      { label: "Home Runs", ...top(hitters, (h) => h.hr, String) },
      { label: "RBI", ...top(hitters, (h) => h.rbi, String) },
      { label: "Stolen Bases", ...top(hitters, (h) => h.sb, String) },
      { label: "ERA", ...top(qualifiedPitchers, (p) => (p.er * 27) / p.outs, (v) => v.toFixed(2), true) },
      { label: "Strikeouts", ...top(pitchers, (p) => p.so, String) },
      { label: "Wins", ...top(pitchers, (p) => p.w, String) },
      { label: "Saves", ...top(pitchers, (p) => p.sv, String) },
    ].filter((leader) => leader.player),
  };
};

const StatGrid = ({ metrics }) => (
  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
    {metrics.map((metric) => (
      <div key={metric.label} className="rounded-xl p-3 bg-jp-section border border-jp-outline/50 text-center">
        <p className="text-jp-muted text-[10px] font-bold uppercase tracking-widest mb-1">{metric.label}</p>
        <p className="text-jp-accent-ink text-lg font-extrabold tabular-nums">{metric.value}</p>
      </div>
    ))}
  </div>
);

export const TeamStatistics = ({ teamId }) => {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: null, stats: null });

  useEffect(() => {
    if (!teamId) return undefined;
    let cancelled = false;
    setState({ loading: true, error: null, stats: null });
    teamService
      .getCoachTeamRoster(teamId, 1, ROSTER_LIMIT, SEASON_YEAR)
      .then((data) => {
        if (!cancelled) setState({ loading: false, error: null, stats: buildTeamStats(data?.players || []) });
      })
      .catch((error) => {
        if (!cancelled) {
          setState({ loading: false, error: error.response?.data?.message || "Couldn't load team statistics.", stats: null });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [teamId]);

  const { loading, error, stats } = state;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-12 h-12 border-4 border-jp-accent border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/40 text-red-600 dark:text-red-400 text-sm">{error}</div>
    );
  }

  if (!stats?.hasHitting && !stats?.hasPitching) {
    return (
      <section className={`${CARD} text-center py-12`}>
        <p className="text-lg font-bold text-jp-ink">No statistics yet</p>
        <p className="text-sm mt-2 text-jp-muted">There are no player stats for this team's 2025–26 season.</p>
      </section>
    );
  }

  const openPlayer = (player) =>
    navigate(`/coach/player/${player._id}`, { state: { from: "teams", playerData: player } });

  return (
    <div className="space-y-6 text-left">
      <p className="text-jp-muted text-xs">
        2025–26 season totals, added up from each player's stats on the roster.
      </p>

      {stats.hasHitting && (
        <section className={CARD}>
          <CardTitle title="Team Hitting" />
          <StatGrid metrics={stats.hitting} />
        </section>
      )}

      {stats.hasPitching && (
        <section className={CARD}>
          <CardTitle title="Team Pitching" />
          <StatGrid metrics={stats.pitching} />
        </section>
      )}

      {stats.leaders.length > 0 && (
        <section className={CARD}>
          <CardTitle title="Team Leaders" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {stats.leaders.map((leader) => (
              <button
                key={leader.label}
                type="button"
                onClick={() => openPlayer(leader.player)}
                className="flex items-center justify-between gap-3 rounded-xl px-4 py-3 bg-jp-section border border-jp-outline/50 hover:border-jp-accent/60 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-jp-accent"
              >
                <span className="min-w-0">
                  <span className="block text-jp-muted text-[10px] font-bold uppercase tracking-widest">{leader.label}</span>
                  <span className="block text-jp-ink text-sm font-semibold truncate">{playerName(leader.player)}</span>
                </span>
                <span className="text-jp-accent-ink text-lg font-extrabold tabular-nums">{leader.value}</span>
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};
